import { RichTextField } from '@components/designer/modal/blocks/RichTextField';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import InputWithLabelT from '@components/tailwind-components/Input/InputWithLabelT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import type { TemplateResponseFormDTO } from '@conversed/rest/src/contracts/template-response/template-response.dto';
import { getTemplateResponseValidationSchema } from '@helpers/validationSchemaHelpers';
import { useModal } from '@hooks/modal/useModal';
import { useValidationForm } from '@hooks/useValidationForm';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  templateResponseData?: Partial<TemplateResponseFormDTO>;
  onSubmit: (data: TemplateResponseFormDTO) => void;
};

export const TemplateResponseDataModal: React.FC<Props> = ({
  templateResponseData,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();

  const templateResponseValidationSchema = useMemo(
    () => getTemplateResponseValidationSchema(),
    [],
  );

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useValidationForm<TemplateResponseFormDTO>({
    schema: templateResponseValidationSchema,
    defaultValues: {
      name: templateResponseData?.name || '',
      content: templateResponseData?.content || '',
      url: templateResponseData?.url || '',
    },
  });

  const initialContent = templateResponseData?.content ?? '';

  const hideAndSubmit = async (data: TemplateResponseFormDTO) => {
    await onSubmit({
      name: data.name?.trim(),
      // * content is HTML from CKEditor — trimming a serialised HTML string has no meaningful effect
      content: data.content,
      url: data.url?.trim(),
    });
    hideModal();
  };

  return (
    <form
      className='tailwind-modal-form'
      onSubmit={handleSubmit(hideAndSubmit)}>
      <div className='tailwind-modal-body'>
        <div className='grid grid-cols-1 gap-x-6 gap-y-2'>
          <div className='col-span-1'>
            <InputWithLabelT
              type='text'
              field='name'
              placeholder={t('settings.template-response.title')}
              label={t('settings.template-response.title')}
              id='template-title'
              control={control}
              inputClassName='text-xs'
              error={errors.name?.message}
            />
          </div>

          <div className='col-span-1'>
            <LabelT
              text={t('settings.template-response.content')}
              className='mb-1'
            />
            <RichTextField
              field='content'
              control={control}
              value={initialContent}
              constantValue={initialContent}
              setValue={setValue}
              disableResize={true}
            />
            {errors.content?.message && (
              <p className='text-red-base text-pico mt-0.5'>
                {errors.content.message}
              </p>
            )}
          </div>

          <div className='col-span-1'>
            <InputWithLabelT
              type='text'
              field='url'
              placeholder={t('settings.template-response.url')}
              label={t('settings.template-response.url')}
              id='url'
              control={control}
              inputClassName='text-xs'
              error={errors.url?.message}
            />
          </div>
        </div>
      </div>
      <div className='tailwind-modal-footer'>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={hideModal}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT type='submit' styling='primary' size='xs' rounding='md'>
          {t('general.buttons.save')}
        </ButtonT>
      </div>
    </form>
  );
};

export default TemplateResponseDataModal;
